import React from 'react';
import { ui } from '../../shared/ui';
import { BUY, SELL, GOLD, eur, qtyShort, type Trigger } from './ptShared';

/**
 * Spot zlata (EUR/g) kroz vrijeme s vodoravnim linijama aktivnih ciljeva.
 * Kupnje su zelene iscrtkane, prodaje crvene iscrtkane; oznaka desno nosi klijenta i kolicinu.
 */
export type SpotPoint = { t: string; p: number; daily: boolean };

export function SpotTargetsChart({ points, targets, height = 300, onPick }: { points: SpotPoint[]; targets: Trigger[]; height?: number; onPick?: (id: number) => void }) {
    const [hover, setHover] = React.useState<number | null>(null);
    const w = 900; const padL = 58; const padR = 170; const padT = 16; const padB = 30;
    const innerW = w - padL - padR; const innerH = height - padT - padB;

    if (points.length === 0) {
        return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: ui.muted, fontSize: '0.88rem' }}>Još nema povijesti spota za odabrano razdoblje.</div>;
    }
    const times = points.map((p) => new Date(p.t.replace(' ', 'T')).getTime());
    const t0 = Math.min(...times); const t1 = Math.max(...times);
    const vals = [...points.map((p) => p.p), ...targets.map((t) => t.target_eur_per_g)];
    let lo = Math.min(...vals); let hi = Math.max(...vals);
    const span = Math.max(hi - lo, hi * 0.01);
    lo -= span * 0.08; hi += span * 0.08;
    const x = (t: number) => padL + (t1 === t0 ? innerW : ((t - t0) / (t1 - t0)) * innerW);
    const y = (v: number) => padT + (1 - (v - lo) / (hi - lo)) * innerH;

    const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(times[i]).toFixed(1)} ${y(p.p).toFixed(1)}`).join(' ');
    const area = `${path} L ${x(times[times.length - 1]).toFixed(1)} ${padT + innerH} L ${x(times[0]).toFixed(1)} ${padT + innerH} Z`;
    const ticks = Array.from({ length: 5 }, (_, i) => lo + ((hi - lo) * i) / 4);
    const xTicks = Array.from({ length: 6 }, (_, i) => t0 + ((t1 - t0) * i) / 5);
    const last = points[points.length - 1];

    // Oznake ciljeva razmaknute da se ne preklapaju.
    const sorted = [...targets].sort((a, b) => b.target_eur_per_g - a.target_eur_per_g);
    const labelY: Record<number, number> = {};
    let prev = -Infinity;
    for (const t of sorted) {
        const yy = Math.max(y(t.target_eur_per_g), prev + 15);
        labelY[t.id] = yy; prev = yy;
    }

    const onMove = (e: React.MouseEvent<SVGRectElement>) => {
        const r = (e.currentTarget as SVGRectElement).getBoundingClientRect();
        const px = ((e.clientX - r.left) / r.width) * innerW;
        const tt = t0 + (px / innerW) * (t1 - t0);
        let best = 0;
        for (let i = 1; i < times.length; i++) if (Math.abs(times[i] - tt) < Math.abs(times[best] - tt)) best = i;
        setHover(best);
    };

    return (
        <svg viewBox={`0 0 ${w} ${height}`} style={{ width: '100%', height, display: 'block' }} preserveAspectRatio="xMidYMid meet">
            <defs>
                <linearGradient id="ptArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GOLD} stopOpacity={0.22} />
                    <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                </linearGradient>
            </defs>
            {ticks.map((v, i) => (
                <g key={i}>
                    <line x1={padL} x2={padL + innerW} y1={y(v)} y2={y(v)} stroke="#eef0f6" />
                    <text x={padL - 8} y={y(v) + 3} textAnchor="end" fontSize="10" fill={ui.muted}>{eur(v, 2)}</text>
                </g>
            ))}
            {xTicks.map((t, i) => (
                <text key={i} x={x(t)} y={height - 9} textAnchor="middle" fontSize="10" fill={ui.muted}>
                    {new Date(t).toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit' })}
                </text>
            ))}
            <path d={area} fill="url(#ptArea)" />
            <path d={path} fill="none" stroke={GOLD} strokeWidth={2.2} strokeLinejoin="round" />

            {targets.map((t) => {
                const c = t.side === 'buy' ? BUY : SELL;
                const yy = y(t.target_eur_per_g);
                return (
                    <g key={t.id} style={{ cursor: onPick ? 'pointer' : 'default' }} onClick={() => onPick?.(t.id)}>
                        <line x1={padL} x2={padL + innerW} y1={yy} y2={yy} stroke={c} strokeWidth={1.3} strokeDasharray="6 4" opacity={0.85} />
                        <line x1={padL + innerW} x2={padL + innerW + 8} y1={yy} y2={labelY[t.id]} stroke={c} strokeWidth={1} opacity={0.6} />
                        <text x={padL + innerW + 11} y={labelY[t.id] + 3.5} fontSize="10.5" fill={c} fontWeight={700}>
                            {t.side === 'buy' ? '▼' : '▲'} {eur(t.target_eur_per_g)} · {t.client_name.length > 14 ? t.client_name.slice(0, 13) + '…' : t.client_name} · {qtyShort(t.quantity_g)}
                            <title>{`${t.side === 'buy' ? 'Kupiti' : 'Prodati'} ${qtyShort(t.quantity_g)} za ${t.client_name} po ${eur(t.target_eur_per_g)} EUR/g`}</title>
                        </text>
                    </g>
                );
            })}

            <circle cx={x(times[times.length - 1])} cy={y(last.p)} r={4.5} fill={GOLD} stroke="#fff" strokeWidth={2} />
            <rect x={padL} y={padT} width={innerW} height={innerH} fill="transparent" onMouseMove={onMove} onMouseLeave={() => setHover(null)} />
            {hover != null ? (() => {
                const hx = x(times[hover]); const hy = y(points[hover].p);
                const label = `${new Date(times[hover]).toLocaleString('hr-HR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}  ${eur(points[hover].p)} EUR/g`;
                const bx = Math.min(hx + 8, padL + innerW - 170);
                return (
                    <g pointerEvents="none">
                        <line x1={hx} x2={hx} y1={padT} y2={padT + innerH} stroke={ui.ink2} strokeDasharray="2 3" opacity={0.5} />
                        <circle cx={hx} cy={hy} r={4} fill="#fff" stroke={GOLD} strokeWidth={2} />
                        <rect x={bx} y={Math.max(padT, hy - 30)} width={166} height={22} rx={6} fill={ui.ink} opacity={0.92} />
                        <text x={bx + 8} y={Math.max(padT, hy - 30) + 15} fontSize="10.5" fill="#fff">{label}</text>
                    </g>
                );
            })() : null}
        </svg>
    );
}
